/** The generic batch-edit dialog (a custom `dialog(ctx)` batch action).
 *
 *  Ordered UPDATE BLOCKS — each an optional flat conditions builder plus a list
 *  of (field, operator, value) mutations — applied per selected row by a
 *  background task. The flow is gated exactly like Find & Replace, and the
 *  backend enforces every step:
 *
 *    configure → RUN DRY RUN (task walks every row, writes nothing, reports
 *    per-row outcomes + a receipt) → review the report inline → APPLY (echoes
 *    the receipt; refused with 409 unless a fresh dry run of the identical
 *    blocks + selection exists; rows changed since the dry run come back
 *    `stale` and are skipped).
 *
 *  Editing anything after a dry run voids the receipt (the backend re-derives
 *  the signature server-side; the UI mirrors it so Apply never offers a call
 *  the backend would refuse).
 */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { runBatch } from '@/api/batch';
import type { BatchEditParams, BatchEditTaskResult } from '@/api/batchEdit';
import { fetchBatchEditCatalog } from '@/api/batchEdit';
import { ConsoleApiError } from '@/api/client';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { useSnackbar } from '@/components/Snackbar';
import { HelpLink } from '@/components/HelpLink';

import { BlockEditor } from './BlockEditor';
import { DryRunReport } from './DryRunReport';
import { blockComplete, buildBlocks, emptyBlock, type BlockDraft } from './model';
import { useBatchTaskResult } from './useBatchTaskResult';

const MIN_REASON = 10;

export interface BatchEditDialogProps {
  ctx: BatchActionContext;
  /** The grid's batch resource slug (must have a batch-edit catalog entry). */
  resource: string;
  /** Human noun for dialog copy ("itinerary", "tour operator"). */
  noun: string;
}

export function BatchEditDialog({ ctx, resource, noun }: BatchEditDialogProps) {
  const snackbar = useSnackbar();
  const [blocks, setBlocks] = useState<BlockDraft[]>(() => [emptyBlock()]);
  const [reason, setReason] = useState('');
  const [dryRunTaskId, setDryRunTaskId] = useState<string | null>(null);

  const catalogQuery = useQuery({
    queryKey: ['batch-edit-catalog'],
    queryFn: fetchBatchEditCatalog,
    staleTime: 5 * 60_000,
  });
  const catalog = catalogQuery.data;
  const resourceEntry = catalog?.resources[resource];
  const paths = useMemo(() => resourceEntry?.paths ?? [], [resourceEntry]);

  const wireBlocks = useMemo(() => buildBlocks(paths, blocks), [paths, blocks]);

  /** Identity of the operation — any change voids the dry run (backend rule,
   *  mirrored so the UI never offers an Apply that would 409). */
  const opIdentity = useMemo(
    () => JSON.stringify({ resource, selection: ctx.selection, blocks: wireBlocks }),
    [resource, ctx.selection, wireBlocks],
  );
  useEffect(() => {
    setDryRunTaskId(null);
  }, [opIdentity]);

  // ---- dry-run task polling + result (404-retried through the archive gap) --
  const dryRun = useBatchTaskResult<BatchEditTaskResult>(dryRunTaskId, 'batch-edit');
  const dryResult = dryRun.result;
  const taskFinished = dryRun.finished;
  const taskSucceeded = dryRun.succeeded;

  // ---- dispatch -------------------------------------------------------------
  const baseParams = useMemo(
    (): Omit<BatchEditParams, 'dry_run'> => ({
      reason: reason.trim(),
      path_type: 'dotty',
      blocks: wireBlocks,
    }),
    [reason, wireBlocks],
  );

  const errorText = (e: unknown) =>
    e instanceof ConsoleApiError
      ? ((e.body as { detail?: string } | undefined)?.detail ?? e.message)
      : (e as Error).message;

  const dryRunMut = useMutation({
    mutationFn: () =>
      runBatch(resource, 'batch_edit', ctx.selection, { ...baseParams, dry_run: true }, ctx.filterQuery),
    onSuccess: (r) => {
      setDryRunTaskId(r.task_id);
      if (r.capped) {
        snackbar.show(
          `Selection capped at ${r.processed.toLocaleString()} of ${r.matched.toLocaleString()} rows.`,
          'warning',
        );
      }
    },
    onError: (e) => snackbar.show(errorText(e), 'error'),
  });

  const applyMut = useMutation({
    mutationFn: () =>
      runBatch(
        resource,
        'batch_edit',
        ctx.selection,
        { ...baseParams, dry_run: false, receipt: dryResult?.receipt },
        ctx.filterQuery,
      ),
    onSuccess: (r) => {
      snackbar.show(
        `Applying to ${r.processed.toLocaleString()} ${noun}(s) in the background — follow it in Background Tasks.`,
        'success',
      );
      ctx.clearSelection();
      ctx.close();
    },
    onError: (e) => snackbar.show(errorText(e), 'error'),
  });

  const busy = dryRunMut.isPending || applyMut.isPending;
  const waitingForDryRun = dryRun.waitingForTask;
  const waitingForResult = dryRun.waitingForResult;
  const blocksOk = blocks.length > 0 && blocks.every((b) => blockComplete(paths, b));
  const reasonOk = reason.trim().length >= MIN_REASON;
  const canApply = dryResult?.receipt != null && reasonOk && !busy;
  const wouldChange = dryResult?.counts.would_change ?? 0;

  const patchBlock = (id: string, patch: Partial<BlockDraft>) =>
    setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  return (
    <Dialog open onClose={busy ? undefined : ctx.close} fullWidth maxWidth="md">
      <DialogTitle>
        Batch edit {ctx.count.toLocaleString()} {noun}(s) <HelpLink story="batch-edit" />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {catalogQuery.isLoading && <LinearProgress />}
          {catalogQuery.isError && (
            <Alert severity="error">The batch-edit catalog failed to load — try reopening the dialog.</Alert>
          )}
          {catalog && !resourceEntry && (
            <Alert severity="error">This grid has no batch-edit catalog entry.</Alert>
          )}

          {resourceEntry && (
            <>
              <Typography variant="body2" color="text.secondary">
                Each update block runs against every selected row: if its conditions match (or it has
                none), its field changes are applied in order. Blocks see the effects of earlier
                blocks. Nothing is written until you apply a reviewed dry run.
              </Typography>

              <Stack spacing={1.5}>
                {blocks.map((b, i) => (
                  <BlockEditor
                    key={b.id}
                    index={i}
                    value={b}
                    paths={paths}
                    catalog={catalog!}
                    canRemove={blocks.length > 1}
                    onChange={(patch) => patchBlock(b.id, patch)}
                    onRemove={() => setBlocks((bs) => bs.filter((x) => x.id !== b.id))}
                  />
                ))}
              </Stack>
              <Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setBlocks((bs) => [...bs, emptyBlock()])}
                >
                  Add update block
                </Button>
              </Box>

              {ctx.selection.mode === 'all_matching' && (
                <Alert severity="info">
                  Acting on {ctx.matchedPhrase} {noun}s
                  {resourceEntry.max_selection
                    ? ` (server cap: ${resourceEntry.max_selection.toLocaleString()} rows per run)`
                    : ''}
                  .
                </Alert>
              )}

              <TextField
                label="Why are you making this change?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                required
                error={reason.length > 0 && !reasonOk}
                helperText={
                  reason.length > 0 && !reasonOk
                    ? `At least ${MIN_REASON} characters.`
                    : 'Recorded in the audit log and in each record’s change history.'
                }
              />

              {/* ---- dry-run state ---- */}
              {waitingForDryRun && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" color="info" label="Dry run in progress" />
                    <Typography variant="caption" color="text.secondary">
                      Walking every selected row without writing…
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant={dryRun.progressPct != null ? 'determinate' : 'indeterminate'}
                    value={dryRun.progressPct ?? 0}
                  />
                </Box>
              )}
              {waitingForResult && <LinearProgress />}
              {taskFinished && !taskSucceeded && (
                <Alert severity="error">
                  The dry run did not complete — check it in Background Tasks, then run it again.
                </Alert>
              )}

              {dryResult && (
                <Box>
                  <Alert severity={wouldChange > 0 ? 'success' : 'warning'} sx={{ mb: 1 }}>
                    <AlertTitle>
                      {wouldChange > 0
                        ? `Dry run reviewed — ${wouldChange.toLocaleString()} row(s) would change`
                        : 'Dry run complete — nothing would change'}
                    </AlertTitle>
                    Editing the blocks or the selection voids this dry run. Rows that change in the
                    meantime are skipped as “changed since dry run”.
                  </Alert>
                  <DryRunReport result={dryResult} taskId={dryRunTaskId as string} />
                </Box>
              )}
              {!dryResult && !waitingForDryRun && !waitingForResult && (
                <Alert severity="info">
                  <AlertTitle>A dry run is required before applying</AlertTitle>
                  The dry run walks every selected row and reports exactly what would change —
                  including why a row would be skipped. Applying without one is refused.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={() => dryRunMut.mutate()}
          disabled={busy || !blocksOk || !reasonOk || waitingForDryRun || waitingForResult}
        >
          Run dry run
        </Button>
        <Button variant="contained" color="warning" onClick={() => applyMut.mutate()} disabled={!canApply}>
          Apply{wouldChange > 0 ? ` to ${wouldChange.toLocaleString()} row(s)` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
